import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';

// Création du contexte
const NavigationContext = createContext(null);

// Hook personnalisé pour utiliser le contexte
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation doit être utilisé dans un NavigationProvider');
  }
  return context;
};

// Provider du contexte
export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();

  // États de navigation
  const [activeTab, setActiveTab] = useState('dashboard');
  const [viewMode, setViewMode] = useState('list');
  const [currentDocId, setCurrentDocId] = useState(null);
  const [docType, setDocType] = useState('quote');
  const [prospectData, setProspectData] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(null);

  // Gérer la création de devis/facture depuis un bien
  useEffect(() => {
    const handleCreateFromProperty = (event) => {
      const propertyData = event.detail;
      setProspectData(propertyData);
      setActiveTab('editor');
    };

    window.addEventListener('createFromProperty', handleCreateFromProperty);
    return () => window.removeEventListener('createFromProperty', handleCreateFromProperty);
  }, []);

  // Détection des paramètres URL pour ouvrir directement un document
  useEffect(() => {
    if (!user || !activeCompanyId) return;

    const urlParams = new URLSearchParams(location.search);
    const docIdParam = urlParams.get('docId');
    const docTypeParam = urlParams.get('docType');
    const tabParam = urlParams.get('tab');
    const openEditorParam = urlParams.get('openEditor');
    const typeParam = urlParams.get('type');

    if (openEditorParam && typeParam) {
      setActiveTab('gestion');
      setViewMode('edit');
      setCurrentDocId(openEditorParam);
      setDocType(typeParam);
      navigate(location.pathname, { replace: true });
    } else if (docIdParam && docTypeParam && tabParam === 'gestion') {
      setActiveTab('gestion');
      setViewMode('edit');
      setCurrentDocId(docIdParam);
      setDocType(docTypeParam);
      navigate(location.pathname, { replace: true });
    }
  }, [user, activeCompanyId, location.search, navigate, location.pathname]);

  // Ouvrir l'éditeur de documents
  const handleOpenEditor = useCallback((id = null, type = 'quote', fromProspect = null) => {
    setCurrentDocId(id);
    setDocType(type);
    setProspectData(fromProspect);
    setViewMode('edit');
    setActiveTab('gestion');
  }, []);

  // Retour à la liste
  const handleBackToList = useCallback(() => {
    setViewMode('list');
    setCurrentDocId(null);
    setProspectData(null);
  }, []);

  // Navigation générale
  const handleNavigate = useCallback((tabOrTarget, siteIdOrFilter = null) => {
    // Si c'est un objet de notification avec target
    if (typeof tabOrTarget === 'object' && tabOrTarget.tab) {
      const { tab, docId, docType: targetDocType } = tabOrTarget;
      setActiveTab(tab);

      if ((tab === 'gestion' || tab === 'devis' || tab === 'factures') && docId) {
        setViewMode('edit');
        setCurrentDocId(docId);
        if (targetDocType) {
          setDocType(targetDocType);
        }
      } else {
        setViewMode('list');
      }
      return;
    }

    // Comportement normal pour une simple chaîne
    const tab = tabOrTarget;
    setActiveTab(tab);
    if (tab === 'gestion' || tab === 'devis' || tab === 'factures') {
      setViewMode('list');
    }
    if (tab === 'chantiers' && siteIdOrFilter) {
      setSelectedSiteId(siteIdOrFilter);
    } else {
      setSelectedSiteId(null);
    }
  }, []);

  const value = {
    // États
    activeTab,
    setActiveTab,
    viewMode,
    setViewMode,
    currentDocId,
    setCurrentDocId,
    docType,
    setDocType,
    prospectData,
    setProspectData,
    selectedSiteId,
    setSelectedSiteId,

    // Actions
    handleOpenEditor,
    handleBackToList,
    handleNavigate
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationContext;
